import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, ViewChild } from '@angular/core';
import { SendMessageForm } from './send-message.form';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommunicationDirectionMap, MessageInterface, SendMessagePayload } from '@sko/models';
import { post } from 'aws-amplify/api';
import { PhoneNumberOption } from '../select-phone-number';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { FormGroupDirective } from '@angular/forms';
// import { LoggerService } from '@sko/ng-logging';
import { ApiService } from '../api.service';
// import { HttpContentType } from '@digitaltoolbuilders/iso';

const AttachmentStatusMap = {
  FAILED: 'failed',
  READY: 'ready',
  UPLOADING: 'uploading',
} as const;

type AttachmentStatus = typeof AttachmentStatusMap[keyof typeof AttachmentStatusMap];

type Attachment = {

  download?: string;

  file: File;

  status: AttachmentStatus;

  upload?: string;

}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-send-message',
    templateUrl: './send-message.component.html',
    styleUrl: './send-message.component.sass',
    standalone: false
})
export class SendMessageComponent {

  private api = inject(ApiService);

  attachments = signal<Array<Attachment>>([]);

  attachmentSizeTotal = computed(() => {

    return this.attachments().reduce((total, att) => {

      return total + att.file.size;

    }, 0);

  });

  form = new SendMessageForm();

  @ViewChild(FormGroupDirective) formGroup: FormGroupDirective;

  from: Signal<string>;

  private fromHintSignal = signal<string>('');

  fromHint = this.fromHintSignal.asReadonly();

  // private logger = inject(LoggerService);

  private processingSignal = signal<boolean>(false);

  processing = this.processingSignal.asReadonly();

  private resultSignal = signal<MessageInterface | undefined>(undefined);

  result = this.resultSignal.asReadonly();

  private snackbar = inject(MatSnackBar);

  private toHintSignal = signal<string>('');

  toHint = this.toHintSignal.asReadonly();

  tooBig = computed(() => {

    return this.attachmentSizeTotal() > 5 * 1024 * 1024;

  });

  constructor() { 
    
    this.from = toSignal(this.form.valueChanges.pipe(map((value) => value.from)), {
      initialValue: this.form.value.from,
    });

  }

  deleteAttachment(att: Attachment) {

    this.attachments.update((curr) => {

      return curr.filter((attachment) => attachment !== att);

    });

  }

  onFileSelected(ev: Event) {

    const el = ev.currentTarget as HTMLInputElement;

    const files = el.files;

    if (files && files.length > 0) {

      const file = files.item(0) as File;

      this.attachments.update((curr) => {

        return curr.concat({
          file,
          status: AttachmentStatusMap.UPLOADING,
        });

      });


      this.api.createAttachement(file.type as any, file.size)
      .then((result) => {

        // this.logger.debug({ result }, 'create attachment success');

        this.attachments.update((curr) => {

          const att = curr.find((att) => att.file === file) as Attachment;

          att.download = result.download;
          att.upload = result.upload;

          return curr.concat([]);

        });

        return this.api.uploadAttachment(result.upload, file);

      })
      .then(() => {

        // this.logger.debug({ }, 'upload success');

        this.attachments.update((curr) => {

          const att = curr.find((att) => att.file === file) as Attachment;

          att.status = AttachmentStatusMap.READY;

          return curr.concat([]);

        });

      })
      .catch((e) => {

        // this.logger.error(e, 'create attachment failed');

        console.error(e);

        this.attachments.update((curr) => {

          const att = curr.find((att) => att.file === file) as Attachment;

          att.status = AttachmentStatusMap.FAILED;

          return curr.concat([]);

        });

      });

    }

  }

  onFromSelected(option: PhoneNumberOption) {

    this.form.patchValue({
      from: option.phone_number,
    });

    this.fromHintSignal.set(option.label);

  }

  onToSelected(option: PhoneNumberOption) {

    this.form.patchValue({
      announce: `Now calling ${option.label}`,
      to: option.phone_number,
    });

    this.toHintSignal.set(option.label);

  }

  submit() {

    if (this.form.valid) {

      this.snackbar.dismiss();

      this.processingSignal.set(true);

      const body: Omit<SendMessagePayload, 'epoch' | 'id' | 'message_sid'> = {
        attachments: this.attachments().filter((att) => att.status === AttachmentStatusMap.READY).map((att) => att.download as string),
        body: this.form.value.body,
        direction: CommunicationDirectionMap.OUTBOUND,
        from: this.form.value.from,
        to: this.form.value.to,
      };

      post({
        apiName: 'default',
        path: '/messages/send',
        options: {
          body,
        },
      })
      .response
      .then((response) => {

        return response.body.json();

      })
      .then((payload) => {

        const result = payload as unknown as MessageInterface;

        this.resultSignal.set(result);

        this.form.reset();

        this.formGroup.resetForm();

        this.snackbar.open(`Message Sent From: ${result.from} To: ${result.to}`, 'Dismiss');

      })
      .catch((e) => {

        console.error(e);

      })
      .finally(() => {

        this.processingSignal.set(false);

      });

    } else {

      this.snackbar.open('The form is invalid. Check for errors.');

    }

  }

}
